import { Flex, Text } from "@chakra-ui/react";
import { Link } from "@reach/router";
import type { FormikHelpers } from "formik";
import { Form, Formik } from "formik";
import { useLocation } from "react-use";
import { CONTACT_US_URL } from "utils/constants";
import * as Yup from "yup";

import UsernamePassword from "newui/features/Auth/components/Login/components/UsernamePassword";

import { FlashError } from "../../../../../components/FormInputs/FlashError";
import { Submit } from "../../../../../components/FormInputs/Submit";
import { OnSubmitValidationError } from "../../../../../utils/OnSubmitValidationError";
import { getErrorMessage } from "api-v2";
import { useState } from "react";
import LoginInviteTokenSelectAccountPath from "./components/LoginInviteTokenSelectAccountPath";
import AuthContainer from "../AuthContainer";
import { LoginPath } from "./constants/TokenReferralConstants";
import GuestSignup from "../GuestSignup/GuestSignup";
import { LoginPathButton } from "./components/LoginPathButton";
import { FormikAcceptTermsCheckbox } from "./FormikAcceptTermsCheckbox";
import { errorMessages } from "./constants/ErrorMessages";
import { useReferralToken } from "../../hooks/useReferralToken";
import { AuthenticateValues, useAuthContext } from "../../context/AuthContextProvider";
import { useDispatch } from "react-redux";

interface MemberLoginProps {
  path: string;
}

interface LoginError {
  message: string;
  status: string;
}

const Login: React.FC<MemberLoginProps> = ({ path: _path }) => {
  const location = useLocation();
  const emailFromState = location?.state?.email as string || "";
  const [loginError, setLoginError] = useState<LoginError | null>(null);
  const { referralToken } = useReferralToken();
  const dispatch = useDispatch();

  const { errorMessage, loginUser } = useAuthContext();

  const handleSubmit = async (
    values: AuthenticateValues,
    formikHelpers: FormikHelpers<AuthenticateValues>
  ) => {
    const { setSubmitting } = formikHelpers;
    setSubmitting(true);

    try {
      setLoginError(null);
      const userResponse = await loginUser(values);
    } catch (e: any) {
      const status = e.response?.status;
      let errorMessage = errorMessages[status]?.description;

      if (e.response) {
        try {
          errorMessage = getErrorMessage(e);
        } catch {
          errorMessage = "Failed to parse error response.";
        }
      } else {
        errorMessage = e.message ?? errorMessage;
      }
      setLoginError({ status: status, message: errorMessage });
    } finally {
      setSubmitting(false);
    }
  };

  if (referralToken && !referralToken.pathChosen) {
    return <AuthContainer additionalHeadingText="">
      <LoginInviteTokenSelectAccountPath tokenType={referralToken?.tokenType} />
    </AuthContainer>
  }

  if (referralToken?.pathChosen === LoginPath.CreateAccount) {
    return <GuestSignup referralToken={referralToken} />
  }

  return (
    <AuthContainer additionalHeadingText="Let’s sign you in.">
      {(errorMessage || loginError) && (
        <FlashError
          heading={
            errorMessages[loginError?.status]?.heading || loginError?.status
          }
          text={
            errorMessage ??
            loginError?.message ??
            errorMessages.invalid.description
          }
          margin="0px 0px 20px 0px"
        />
      )}
      <Formik
        enableReinitialize
        initialValues={{
          email: emailFromState,
          password: "",
          inviteToken: referralToken?.tokenValue,
          tokenType: referralToken?.tokenType,
        } as AuthenticateValues}
        validationSchema={Yup.object({
          email: Yup.string().email().required(),
          password: Yup.string().required(),
        })}
        onSubmit={handleSubmit}
      >
        {({ setFieldValue, isSubmitting, setStatus }) => {
          const onSubmitValidationError = () => setStatus("required");
          return (
            <Form autoComplete="on">
              <UsernamePassword
                setFieldValue={setFieldValue}
                emailDataTestid="login-email"
                passwordDataTestid="login-password"
              />

              <Flex className="mb-5 mt-3 justify-between text-sm">
                <FormikAcceptTermsCheckbox
                  label="Remember me"
                  name="remember_me"
                  testId="login-remember"
                />
                <Link
                  to="/reset-password"
                  className="text-blue-500 underline"
                >
                  Forgot my password
                </Link>
              </Flex>

              <Submit
                type="submit"
                widthExpand
                marginTop="24px"
                isSubmitting={isSubmitting}
                text="Sign In"
                submittingText="Logging in..."
                style={{ textTransform: "uppercase", lineHeight: "1.25" }}
                testId="login-button"
              />

              <OnSubmitValidationError callback={onSubmitValidationError} />
            </Form>
          );
        }}
      </Formik>
      <Text className="mt-5 text-center text-sm text-gray-400">
        Not registered yet?{" "}
        <a
          href={CONTACT_US_URL}
          target="_blank"
          rel="noopener noreferrer"
          className="text-blue-400 underline"
        >
          Connect with us
        </a>
      </Text>
      <LoginPathButton loginPath={LoginPath.CreateAccount} showReferralText />
    </AuthContainer>
  );
};

export default Login;
